
import React, { Component } from 'react';

import logger from '../../../app/logger';
import { safePlayMediaElement } from '../../playbackUtils';

/**
 * The type of the React {@code Component} props of {@link Video}.
 */
type Props = {

    /**
     * CSS classes to add to the video element.
     */
    className: string,

    /**
     * The value of the id attribute of the video. Used by the torture tests to
     * locate video elements.
     */
    id: string,

    /**
     * Optional callback to invoke once the video starts playing.
     */
    onVideoPlaying?: Function,

    /**
     * The MHLocalTrack to display.
     */
    videoTrack: ?Object,

    /**
     * Used to determine the value of the autoplay attribute of the underlying
     * video element.
     */
    autoPlay: boolean,

    /**
     * Used to determine the value of the autoplay attribute of the underlying
     * video element.
     */
    playsinline: boolean
};

/**
 * Component that renders a video element for a passed in video track.
 *
 * @augments Component
 */
class Video extends Component<Props> {
    _videoElement: ?Object;
    _isUnmounted: boolean;
    _autoplayBlockedLogged: boolean;
    _playRetryScheduled: boolean;

    /**
     * Default values for {@code Video} component's properties.
     *
     * @static
     */
    static defaultProps = {
        className: '',
        autoPlay: true,
        id: '',
        playsinline: true
    };

    /**
     * Initializes a new {@code Video} instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        /**
         * The internal reference to the DOM/HTML element intended for
         * displaying a video.
         *
         * @private
         * @type {HTMLVideoElement}
         */
        this._videoElement = null;
        this._isUnmounted = false;
        this._autoplayBlockedLogged = false;
        this._playRetryScheduled = false;

        // Bind event handlers so they are only bound once for every instance.
        this._onVideoPlaying = this._onVideoPlaying.bind(this);
        this._setVideoElement = this._setVideoElement.bind(this);
    }

    /**
     * Invokes the library for rendering the video on initial display. Sets the
     * volume level to zero to ensure no sound plays.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        this._isUnmounted = false;

        if (this._videoElement) {
            this._videoElement.volume = 0;

            // Add safety check to prevent crashes during room switching
            if (typeof this._videoElement.onplaying !== 'undefined') {
                this._videoElement.onplaying = this._onVideoPlaying;
            }
        }

        this._attachTrack(this.props.videoTrack);

        if (this._videoElement && this.props.autoPlay) {
            this._safePlayVideoElement();
        }
    }

    /**
     * Remove any existing associations between the current video track and the
     * component's video element.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        this._isUnmounted = true;
        this._trackDetached = true; // Guard against double detach

        // Clean up event handlers to prevent memory leaks
        if (this._videoElement && typeof this._videoElement.onplaying !== 'undefined') {
            this._videoElement.onplaying = null;
        }

        this._detachTrack(this.props.videoTrack);
    }

    /**
     * Updates the video display only if a new track is added. This component's
     * updating is blackboxed from React to prevent re-rendering of video
     * element, as the lib uses {@code track.attach(videoElement)} instead.
     *
     * @inheritdoc
     * @returns {boolean} - False is always returned to blackbox this component
     * from React.
     */
    shouldComponentUpdate(nextProps) {
        const currentMHTrack = this.props.videoTrack
            && this.props.videoTrack.mhTrack;
        const nextMHTrack = nextProps.videoTrack
            && nextProps.videoTrack.mhTrack;

        if (currentMHTrack !== nextMHTrack) {
            // Guard against double detach during component unmounting
            if (!this._trackDetached) {
                this._detachTrack(this.props.videoTrack);
            }
            this._trackDetached = false; // Reset for new track attachment
            this._attachTrack(nextProps.videoTrack);
        }

        return false;
    }

    /**
     * Renders the video element.
     *
     * @override
     * @returns {ReactElement}
     */
    render() {
        return (
            <video
                autoPlay = { this.props.autoPlay }
                className = { this.props.className }
                id = { this.props.id }
                playsInline = { this.props.playsinline }
                ref = { this._setVideoElement } />
        );
    }

    /**
     * Calls into the passed in track to associate the track with the
     * component's video element and render video.
     *
     * @param {Object} videoTrack - The redux representation of the
     * {@code MHLocalTrack}.
     * @private
     * @returns {void}
     */
    _attachTrack(videoTrack) {
        if (!videoTrack || !videoTrack.mhTrack) {
            return;
        }

        videoTrack.mhTrack.attach(this._videoElement);
    }

    /**
     * Removes the association to the component's video element from the passed
     * in redux representation of jitsi video track to stop the track from
     * rendering.
     *
     * @param {Object} videoTrack -  The redux representation of the
     * {@code MHLocalTrack}.
     * @private
     * @returns {void}
     */
    _detachTrack(videoTrack) {
        if (this._videoElement && videoTrack && videoTrack.mhTrack) {
            videoTrack.mhTrack.detach(this._videoElement);
        }
    }

    _onVideoPlaying: () => void;

    /**
     * Invokes the onvideoplaying callback if defined.
     *
     * @private
     * @returns {void}
     */
    _onVideoPlaying() {
        if (this.props.onVideoPlaying) {
            this.props.onVideoPlaying();
        }
    }

    _setVideoElement: () => void;

    /**
     * Sets an instance variable for the component's video element so it can be
     * referenced later for attaching and detaching a MHLocalTrack.
     *
     * @param {Object} element - DOM element for the component's video display.
     * @private
     * @returns {void}
     */
    _setVideoElement(element) {
        this._videoElement = element;
    }

    /**
     * Calls play() safely and ignores expected reflow/unmount interruption errors.
     *
     * @private
     * @returns {void}
     */
    _safePlayVideoElement() {
        const element = this._videoElement;

        if (!element || this._isUnmounted) {
            return;
        }

        // The video element can be momentarily detached from the DOM while the
        // large video / PiP containers are reparented. Retry once on the next
        // frame so the explicit play() is not permanently skipped (the browser
        // autoplay attribute remains the primary playback trigger).
        if (!element.isConnected) {
            this._schedulePlayRetry();

            return;
        }

        safePlayMediaElement(element, {
            onExpectedInterruption: error => {
                if (this._isExpectedPlayInterruption(error)) {
                    return;
                }

                logger.error('Video play failed:', error);
            },
            onAutoplayBlocked: error => {
                // Log once per component instance to avoid noisy repeats during fast re-renders.
                if (!this._autoplayBlockedLogged) {
                    this._autoplayBlockedLogged = true;
                    logger.warn('Video play blocked by browser policy:', error);
                }
            },
            onError: error => {
                logger.error('Video play failed:', error);
            }
        });
    }

    /**
     * Returns true for benign play() failures that happen during DOM churn.
     *
     * @param {Object} error - The play() rejection error.
     * @private
     * @returns {boolean}
     */
    _isExpectedPlayInterruption(error) {
        const message = error?.message || '';

        return this._isUnmounted
            || !this._videoElement
            || !this._videoElement.isConnected
            || error?.name === 'AbortError'
            || message.includes('play() request was interrupted')
            || message.includes('media was removed from the document');
    }

    /**
     * Schedules a single deferred play() attempt for the next frame. Used when
     * the element is temporarily detached during container reparenting. Only
     * one retry can be pending at a time so this can never loop.
     *
     * @private
     * @returns {void}
     */
    _schedulePlayRetry() {
        if (this._isUnmounted || this._playRetryScheduled) {
            return;
        }

        this._playRetryScheduled = true;

        const retry = () => {
            this._playRetryScheduled = false;

            if (this._isUnmounted) {
                return;
            }

            const element = this._videoElement;

            if (element && element.isConnected) {
                this._safePlayVideoElement();
            }
        };

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(retry);
        } else {
            setTimeout(retry, 0);
        }
    }
}

export default Video;
